// src/components/PublicAccessCodeManager.tsx
import React, { useState, useEffect } from 'react';
import { businessAccessCodeApi } from '../services/api';
import { Key, Save, Eye, EyeOff, RefreshCw, Lock, Shield } from 'lucide-react';
import { notifySuccess, notifyError } from '../utils/Toastify';

const PublicAccessCodeManager: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState<any>(null);
  const [accessCode, setAccessCode] = useState('');
  const [showCode, setShowCode] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const response = await businessAccessCodeApi.getSettings();
      setSettings(response.data.data);
      setAccessCode(''); // Don't show existing code for security
    } catch (error: any) {
      console.error('Failed to load settings:', error);
      if (error.response?.status !== 403) {
        notifyError('Failed to load access code settings');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!accessCode.trim()) {
      notifyError('Please enter an access code');
      return;
    }

    if (accessCode.length < 6 || accessCode.length > 50) {
      notifyError('Code must be 6-50 characters long');
      return;
    }

    if (!/^[a-zA-Z0-9-]+$/.test(accessCode)) {
      notifyError('Code must contain only letters, numbers, and dashes');
      return;
    }

    try {
      setSaving(true);
      await businessAccessCodeApi.updateCode(accessCode);
      notifySuccess('Public access code updated successfully');
      setIsDirty(false);
      await loadSettings();
      setAccessCode('');
      setShowCode(false);
    } catch (error: any) {
      console.error('Failed to update code:', error);
      notifyError(
        error.response?.data?.error?.message || 'Failed to update access code'
      );
    } finally {
      setSaving(false);
    }
  };

  const generateRandomCode = () => {
    const chars = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let code = '';
    for (let i = 0; i < 20; i++) {
      code += chars[Math.floor(Math.random() * chars.length)];
    }
    setAccessCode(code);
    setShowCode(true);
    setIsDirty(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#8b3dff]"></div>
      </div>
    );
  }

  if (!settings) {
    return null; // No access or error
  }

  return (
    <div className="card !bg-[#1e293b] !border-[#334155] border">
      <div className="p-6">
        <div className="flex items-center space-x-3 mb-4">
          <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
            <Shield className="w-5 h-5 text-purple-400" />
          </div>
          <div>
            <h3 className="text-lg font-medium text-white">
              Public Access Code
            </h3>
            <p className="text-sm text-[#64748b]">
              Manage access code for unauthenticated public chat users
            </p>
          </div>
        </div>

        {/* Business Info */}
        <div className="mb-6 p-4 bg-[#0f172a] border border-[#334155] rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-[#64748b]">Business</p>
              <p className="text-white font-medium">{settings.businessName}</p>
            </div>
            <div className="text-right">
              <p className="text-sm text-[#64748b]">Status</p>
              <div className="flex items-center space-x-2">
                {settings.publicAccessEnabled ? (
                  <>
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                    <span className="text-green-400 text-sm">Enabled</span>
                  </>
                ) : (
                  <>
                    <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                    <span className="text-red-400 text-sm">Disabled</span>
                  </>
                )}
              </div>
            </div>
          </div>
          {settings.hasAccessCode && (
            <div className="mt-3 pt-3 border-t border-[#334155]">
              <p className="text-xs text-[#64748b]">
                Current code length: {settings.accessCodeLength} characters
              </p>
            </div>
          )}
        </div>

        {/* Access Code Input */}
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-white mb-2">
              New Access Code
            </label>
            <div className="flex space-x-2">
              <div className="flex-1 relative">
                <input
                  type={showCode ? 'text' : 'password'}
                  value={accessCode}
                  onChange={(e) => {
                    setAccessCode(e.target.value);
                    setIsDirty(true);
                  }}
                  placeholder="Enter 6-50 character code"
                  className="w-full bg-[#0f172a] border border-[#334155] rounded-lg px-4 py-3 pr-10 text-white placeholder-[#64748b] focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  maxLength={50}
                />
                <button
                  type="button"
                  onClick={() => setShowCode(!showCode)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#64748b] hover:text-white"
                >
                  {showCode ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <button
                onClick={generateRandomCode}
                className="px-4 py-3 bg-[#0f172a] border border-[#334155] rounded-lg text-white hover:bg-[#1e293b] transition-colors flex items-center space-x-2"
                title="Generate random code"
              >
                <RefreshCw className="w-4 h-4" />
                <span className="hidden sm:inline">Generate</span>
              </button>
            </div>
            <p className="text-xs text-[#64748b] mt-2">
              Must be 6-50 characters (letters, numbers, and dashes only)
            </p>
          </div>

          {/* Save Button */}
          <div className="flex justify-end">
            <button
              onClick={handleSave}
              disabled={!isDirty || saving || !accessCode.trim()}
              className="px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg hover:from-purple-700 hover:to-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 transition-all"
            >
              {saving ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  <span>Save Access Code</span>
                </>
              )}
            </button>
          </div>
        </div>

        {/* Info */}
        <div className="mt-6 p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
          <div className="flex items-start space-x-3">
            <Lock className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-blue-300 font-medium">Security Notice</p>
              <p className="text-xs text-blue-200 mt-1">
                This code allows unauthenticated users to perform agentic actions in public chat.
                Keep it secure and share only with authorized personnel.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PublicAccessCodeManager;